import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useFormContext} from "react-hook-form";
import {FilePond, registerPlugin} from "react-filepond";
import DocumentsHolder from "v4/components/form/Field/components/InputFile/components/DocumentsHolder/DocumentsHolder";
import FileDTO, {isFileDTOList} from "v4/DTO/FileDTO";
import {usePolTranslation} from "v4/hooks/usePolTranslation";

registerPlugin(FilePondPluginFileValidateSize, FilePondPluginFileValidateType);

export default React.forwardRef(function InputFile({
                                                       onChange,
                                                       fieldName,
                                                       fieldValue,
                                                       multiple,
                                                       type,
                                                       properties,
                                                       htmlConstraints,
                                                       fileConstraints,
                                                   }, ref) {
    const {t} = usePolTranslation();
    const {resetField, getValues} = useFormContext();
    const [files, setFiles] = useState(() => FileDTO.normalize(fieldValue, {type})
        .filter(dto => dto.isNew)
        .map(dto => dto.file));
    const [revalidate, setRevalidate] = useState(false);
    const wrapperRef = useRef(null);

    const dtos = useMemo(() => FileDTO.normalize(fieldValue, {type}), [fieldValue, type]);
    const serverFiles = useMemo(() => dtos
        .map((dto, index) => ({dto, index}))
        .filter(({dto}) => !dto.isNew && !dto.isQuotePdf), [dtos]);

    const FileConstraints = fileConstraints ?? properties?.customerFile?.constraints?.File;

    const constraints = {
        ...htmlConstraints,
        maxFileSize: FileConstraints?.maxSize ? FileConstraints.maxSize + 'B' : process.env.REACT_APP_MAX_FILES_SIZE_MB_UPLOAD + 'MB',
        maxTotalFileSize: FileConstraints?.maxSize ? FileConstraints.maxSize + 'B' : process.env.REACT_APP_MAX_FILES_SIZE_MB_UPLOAD + 'MB',
        allowMultiple: multiple,
        acceptedFileTypes: FileConstraints?.mimeTypes ?? ['application/pdf'],
    }

    useEffect(() => {
        const value = getValues(fieldName);
        if (isFileDTOList(value)) return;

        resetField(fieldName, {defaultValue: FileDTO.normalize(value, {type})});
    }, [fieldName, type, getValues, resetField]);

    const updateFiles = items => {
        setFiles(items);
        // react-filepond only binds onupdatefiles on mount: read the live value, not this render's closure
        onChange([
            ...FileDTO.normalize(getValues(fieldName), {type}).filter(dto => !dto.isNew),
            ...items.map(item => FileDTO.fromFile(item.file, {type, error: item.getMetadata('error')})),
        ]);
    };

    const flagFileError = (item, error) => {
        item.setMetadata('error', error);
        onChange(FileDTO.normalize(getValues(fieldName), {type})
            .map(dto => dto.file === item.file ? dto.withError(error) : dto));
    };

    const toggleServerFileDelete = (index, shouldDelete) => {
        onChange(dtos.map((dto, dtoIndex) => dtoIndex === index ? dto.withShouldDelete(shouldDelete) : dto));
    };

    return (
        <>
            {/* Adding wrapper to force height when Filepond is removed from DOM */}
            <div ref={wrapperRef}>
                {revalidate
                    /* To force revalidation Filepond is removed from DOM */
                    ? setTimeout(() => setRevalidate(false), 0) && null
                    : (
                        <FilePond className="filepond-v4"
                                  ref={ref}
                                  files={files}
                                  onupdatefiles={updateFiles}
                                  onaddfile={(error, file) => {
                                      if (!error || !file) return;
                                      flagFileError(file, error.main);
                                  }}
                                  beforeRemoveFile={() => {
                                      // Force keeping height of the wrapper without transition
                                      wrapperRef.current.style.transition = '';
                                      wrapperRef.current.style.minHeight = wrapperRef.current.offsetHeight + 'px';
                                  }}
                                  onremovefile={(error, file) => {
                                      updateFiles(files.filter(f => f.id !== file.id));
                                      setRevalidate(true);
                                  }}
                                  oninit={() => {
                                      if (!wrapperRef.current) return;
                                      // Remove height of the wrapper with transition
                                      wrapperRef.current.style.transition = 'min-height 0.2s ease-in-out';
                                      wrapperRef.current.style.minHeight = '0';
                                  }}
                                  allowFileSizeValidation={true}
                                  {...constraints}
                                  labelMaxTotalFileSizeExceeded={t('maximum_size_exceeded_label')}
                                  labelMaxTotalFileSize={t('maximum_size_label')}
                                  labelMaxFileSizeExceeded={t('maximum_size_exceeded_label')}
                                  labelMaxFileSize={t('maximum_size_label')}
                                  labelIdle={t('add_files')}
                                  labelFileTypeNotAllowed={t('filepond_wrong_filetype')}
                                  fileValidateTypeLabelExpectedTypes={t('filepond_expect_filetype')}
                        />
                    )}
            </div>
            {serverFiles.length > 0 && (
                <DocumentsHolder multiple={multiple} serverFiles={serverFiles} fieldName={fieldName}
                                 onToggleDelete={toggleServerFileDelete}/>
            )}
        </>
    )
})
